/**
 * ArrivalsPanel - Slide-out panel showing expected arrivals, departures & returning aircraft
 *
 * Sections:
 * 1. Inbound Today — allocations where presence = 'expected' and starts_at <= today
 * 2. Expected Returns — allocations where presence = 'away' with expected_return_at
 * 3. Upcoming (Next 7 days) — reserved allocations with starts_at within 7 days
 *
 * Also allows recording new departures/arrivals via aircraft search dropdown.
 * Data sourced from enrichedData (client-side filtering, no new queries).
 */

import { useMemo, useState } from 'react'
import { PlaneLanding, PlaneTakeoff, Clock, CalendarDays, LogIn, ChevronsUpDown, CalendarClock, Radar } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { useRampPanelStore } from '@/stores/rampPanelStore'
import { useCheckInEvent } from '@/hooks/useAllocationEvents'
import { useLocationSchedules } from '@/hooks/useLocationSchedules'
import { formatDate, formatRelativeDate, getDaysUntil } from '@/lib/utils/format-business-data'
import { DepartureDialog } from '@/components/ramp-wizard/DepartureDialog'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { AircraftSchedule } from '@/types/aircraft-schedules'
import { SCHEDULE_CONFIDENCE_LABELS, SCHEDULE_SOURCE_LABELS } from '@/types/aircraft-schedules'
import { toast } from 'sonner'

interface ArrivalsPanelProps {
  enrichedData: Map<string, AllocationEnrichedData>
  onFocusAircraft: (allocationId: string) => void
  locationId: string
}

function getContractBadge(data: AllocationEnrichedData) {
  if (data.leaseContract) {
    return { label: 'Lease', className: 'bg-blue-600' }
  }
  if (data.transientStay) {
    return { label: 'Transient', className: 'bg-amber-600' }
  }
  return null
}

function getClientName(data: AllocationEnrichedData): string {
  if (data.leaseContract?.clientName) return data.leaseContract.clientName
  if (data.transientStay?.clientName) return data.transientStay.clientName
  const owner = data.aircraft.owner
  if (owner?.companyName) return owner.companyName
  if (owner?.firstName || owner?.lastName) {
    return [owner.firstName, owner.lastName].filter(Boolean).join(' ')
  }
  return data.aircraft.ownerName || '-'
}

interface SectionItem {
  data: AllocationEnrichedData
  dateLabel: string
  dateTooltip: string
}

export function ArrivalsPanel({ enrichedData, onFocusAircraft, locationId }: ArrivalsPanelProps) {
  const { activePanel, closePanel } = useRampPanelStore()
  const isOpen = activePanel === 'arrivals'
  const checkInMutation = useCheckInEvent()

  // Dialog state
  const [departureOpen, setDepartureOpen] = useState(false)
  const [arrivalOpen, setArrivalOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [selectedAircraft, setSelectedAircraft] = useState<AllocationEnrichedData | null>(null)

  // Combobox state
  const [depSearchOpen, setDepSearchOpen] = useState(false)
  const [arrSearchOpen, setArrSearchOpen] = useState(false)
  const [schedSearchOpen, setSchedSearchOpen] = useState(false)

  // Filter aircraft by presence for action buttons
  const { presentAircraft, awayAircraft, allActiveAircraft } = useMemo(() => {
    const present: AllocationEnrichedData[] = []
    const away: AllocationEnrichedData[] = []
    const allActive: AllocationEnrichedData[] = []
    for (const data of enrichedData.values()) {
      if (data.allocation.status === 'active' || data.allocation.status === 'reserved') {
        allActive.push(data)
      }
      if (data.allocation.presence === 'present' && data.allocation.status === 'active') {
        present.push(data)
      }
      if (data.allocation.presence === 'away' && data.allocation.status === 'active') {
        away.push(data)
      }
    }
    present.sort((a, b) => a.aircraft.tailNumber.localeCompare(b.aircraft.tailNumber))
    away.sort((a, b) => a.aircraft.tailNumber.localeCompare(b.aircraft.tailNumber))
    allActive.sort((a, b) => a.aircraft.tailNumber.localeCompare(b.aircraft.tailNumber))
    return { presentAircraft: present, awayAircraft: away, allActiveAircraft: allActive }
  }, [enrichedData])

  // Fetch today's schedules for this location
  const { data: locationSchedules } = useLocationSchedules(locationId, new Date())

  // Build set of aircraft IDs that have allocations
  const allocatedAircraftIds = useMemo(() => {
    const ids = new Set<string>()
    for (const data of enrichedData.values()) {
      ids.add(data.aircraft.id)
    }
    return ids
  }, [enrichedData])

  // Schedule-only items: schedules for aircraft WITHOUT allocations
  const { scheduledArrivals, scheduledDepartures } = useMemo(() => {
    const arrivals: AircraftSchedule[] = []
    const departures: AircraftSchedule[] = []
    if (!locationSchedules) return { scheduledArrivals: arrivals, scheduledDepartures: departures }

    for (const sched of locationSchedules) {
      if (allocatedAircraftIds.has(sched.aircraftId)) continue
      if (sched.status === 'completed' || sched.status === 'cancelled') continue
      if (sched.direction === 'inbound') {
        arrivals.push(sched)
      } else {
        departures.push(sched)
      }
    }

    arrivals.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    departures.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    return { scheduledArrivals: arrivals, scheduledDepartures: departures }
  }, [locationSchedules, allocatedAircraftIds])

  const { inboundToday, expectedReturns, upcoming } = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const todayEnd = new Date(now)
    todayEnd.setHours(23, 59, 59, 999)
    const weekFromNow = new Date(now)
    weekFromNow.setDate(weekFromNow.getDate() + 7)

    const inbound: SectionItem[] = []
    const returns: SectionItem[] = []
    const upcomingList: SectionItem[] = []

    for (const data of enrichedData.values()) {
      const { status, presence, startsAt, expectedReturnAt } = data.allocation

      // Inbound Today: expected presence, starts_at <= today
      if (presence === 'expected' && startsAt) {
        const startDate = new Date(startsAt)
        if (startDate <= todayEnd) {
          inbound.push({
            data,
            dateLabel: formatRelativeDate(startsAt),
            dateTooltip: `Arrival: ${formatDate(startsAt)}`,
          })
        } else if (status === 'reserved' && startDate <= weekFromNow) {
          upcomingList.push({
            data,
            dateLabel: formatDate(startsAt),
            dateTooltip: `Expected ${formatRelativeDate(startsAt)}`,
          })
        }
      }

      // Expected Returns: away presence with expected_return_at
      if (presence === 'away' && expectedReturnAt) {
        returns.push({
          data,
          dateLabel: formatRelativeDate(expectedReturnAt),
          dateTooltip: `Returns: ${formatDate(expectedReturnAt)}`,
        })
      }
    }

    returns.sort((a, b) => {
      const aDate = a.data.allocation.expectedReturnAt!
      const bDate = b.data.allocation.expectedReturnAt!
      return new Date(aDate).getTime() - new Date(bDate).getTime()
    })

    upcomingList.sort((a, b) => {
      const aDate = a.data.allocation.startsAt!
      const bDate = b.data.allocation.startsAt!
      return new Date(aDate).getTime() - new Date(bDate).getTime()
    })

    return {
      inboundToday: inbound,
      expectedReturns: returns,
      upcoming: upcomingList,
    }
  }, [enrichedData])

  const totalCount = inboundToday.length + expectedReturns.length + upcoming.length + scheduledArrivals.length + scheduledDepartures.length

  const handleCheckIn = async (item: SectionItem) => {
    const { data } = item
    try {
      await checkInMutation.mutateAsync({
        allocation_id: data.allocationId,
        aircraft_id: data.aircraft.id,
        location_id: locationId,
        transient_stay_id: data.allocation.transientStayId,
      })
      toast.success(`${data.aircraft.tailNumber} checked in`)
    } catch {
      toast.error('Failed to check in aircraft')
    }
  }

  const handleSelectForDeparture = (data: AllocationEnrichedData) => {
    setSelectedAircraft(data)
    setDepSearchOpen(false)
    setDepartureOpen(true)
  }

  const handleSelectForArrival = (data: AllocationEnrichedData) => {
    setSelectedAircraft(data)
    setArrSearchOpen(false)
    setArrivalOpen(true)
  }

  const handleSelectForSchedule = (data: AllocationEnrichedData) => {
    setSelectedAircraft(data)
    setSchedSearchOpen(false)
    setScheduleOpen(true)
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && closePanel()}>
        <SheetContent side="left" className="w-[360px] p-0">
          <SheetHeader className="px-4 py-3 border-b">
            <SheetTitle className="flex items-center gap-2 text-base">
              <PlaneLanding className="w-4 h-4" />
              Arrivals & Returns
              <Badge variant="secondary" className="ml-auto text-xs">
                {totalCount}
              </Badge>
            </SheetTitle>

            {/* Quick Actions: Record Departure / Arrival */}
            <div className="flex gap-2 mt-2">
              <Popover open={depSearchOpen} onOpenChange={setDepSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs justify-between"
                    disabled={presentAircraft.length === 0}
                  >
                    <span className="flex items-center gap-1.5">
                      <PlaneTakeoff className="w-3.5 h-3.5" />
                      Record Departure
                    </span>
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search tail number..." />
                    <CommandList>
                      <CommandEmpty>No aircraft found</CommandEmpty>
                      <CommandGroup heading="Present Aircraft">
                        {presentAircraft.map((data) => (
                          <CommandItem
                            key={data.allocationId}
                            value={`${data.aircraft.tailNumber} ${data.aircraft.typeCode} ${getClientName(data)}`}
                            onSelect={() => handleSelectForDeparture(data)}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <span className="font-medium">{data.aircraft.tailNumber}</span>
                                <span className="text-muted-foreground ml-2 text-xs">{data.aircraft.typeCode}</span>
                              </div>
                              <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                                {getClientName(data)}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={arrSearchOpen} onOpenChange={setArrSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs justify-between"
                    disabled={awayAircraft.length === 0}
                  >
                    <span className="flex items-center gap-1.5">
                      <PlaneLanding className="w-3.5 h-3.5" />
                      Record Arrival
                    </span>
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search tail number..." />
                    <CommandList>
                      <CommandEmpty>No aircraft found</CommandEmpty>
                      <CommandGroup heading="Away Aircraft">
                        {awayAircraft.map((data) => (
                          <CommandItem
                            key={data.allocationId}
                            value={`${data.aircraft.tailNumber} ${data.aircraft.typeCode} ${getClientName(data)}`}
                            onSelect={() => handleSelectForArrival(data)}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <span className="font-medium">{data.aircraft.tailNumber}</span>
                                <span className="text-muted-foreground ml-2 text-xs">{data.aircraft.typeCode}</span>
                              </div>
                              <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                                {getClientName(data)}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Schedule a future departure/arrival */}
            <Popover open={schedSearchOpen} onOpenChange={setSchedSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-8 text-xs justify-between"
                  disabled={allActiveAircraft.length === 0}
                >
                  <span className="flex items-center gap-1.5">
                    <CalendarClock className="w-3.5 h-3.5" />
                    Schedule Departure / Arrival
                  </span>
                  <ChevronsUpDown className="w-3 h-3 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search tail number..." />
                  <CommandList>
                    <CommandEmpty>No aircraft found</CommandEmpty>
                    <CommandGroup heading="All Aircraft">
                      {allActiveAircraft.map((data) => (
                        <CommandItem
                          key={data.allocationId}
                          value={`${data.aircraft.tailNumber} ${data.aircraft.typeCode} ${getClientName(data)}`}
                          onSelect={() => handleSelectForSchedule(data)}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div>
                              <span className="font-medium">{data.aircraft.tailNumber}</span>
                              <span className="text-muted-foreground ml-2 text-xs">{data.aircraft.typeCode}</span>
                            </div>
                            <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                              {getClientName(data)}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-130px)]">
            {/* Section 1: Inbound Today */}
            {inboundToday.length > 0 && (
              <Section
                title="Inbound Today"
                icon={<PlaneLanding className="w-3.5 h-3.5" />}
                count={inboundToday.length}
                color="blue"
              >
                {inboundToday.map((item) => (
                  <ArrivalCard
                    key={item.data.allocationId}
                    item={item}
                    showCheckIn
                    onFocus={() => {
                      onFocusAircraft(item.data.allocationId)
                      closePanel()
                    }}
                    onCheckIn={() => handleCheckIn(item)}
                    checkInPending={checkInMutation.isPending}
                  />
                ))}
              </Section>
            )}

            {/* Section 2: Expected Returns */}
            {expectedReturns.length > 0 && (
              <Section
                title="Expected Returns"
                icon={<Clock className="w-3.5 h-3.5" />}
                count={expectedReturns.length}
                color="green"
              >
                {expectedReturns.map((item) => (
                  <ArrivalCard
                    key={item.data.allocationId}
                    item={item}
                    onFocus={() => {
                      onFocusAircraft(item.data.allocationId)
                      closePanel()
                    }}
                  />
                ))}
              </Section>
            )}

            {/* Section 3: Upcoming (Next 7 Days) */}
            {upcoming.length > 0 && (
              <Section
                title="Upcoming (7 Days)"
                icon={<CalendarDays className="w-3.5 h-3.5" />}
                count={upcoming.length}
                color="gray"
              >
                {upcoming.map((item) => (
                  <ArrivalCard
                    key={item.data.allocationId}
                    item={item}
                    onFocus={() => {
                      onFocusAircraft(item.data.allocationId)
                      closePanel()
                    }}
                  />
                ))}
              </Section>
            )}

            {/* Section 4: Scheduled Arrivals (no allocation) */}
            {scheduledArrivals.length > 0 && (
              <Section
                title="Scheduled Arrivals"
                icon={<Radar className="w-3.5 h-3.5" />}
                count={scheduledArrivals.length}
                color="purple"
              >
                {scheduledArrivals.map((sched) => (
                  <ScheduleCard key={sched.id} schedule={sched} />
                ))}
              </Section>
            )}

            {/* Section 5: Scheduled Departures (no allocation) */}
            {scheduledDepartures.length > 0 && (
              <Section
                title="Scheduled Departures"
                icon={<Radar className="w-3.5 h-3.5" />}
                count={scheduledDepartures.length}
                color="amber"
              >
                {scheduledDepartures.map((sched) => (
                  <ScheduleCard key={sched.id} schedule={sched} />
                ))}
              </Section>
            )}

            {totalCount === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No expected arrivals or returns
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Departure Dialog */}
      {selectedAircraft && (
        <DepartureDialog
          open={departureOpen}
          onOpenChange={setDepartureOpen}
          tailNumber={selectedAircraft.aircraft.tailNumber}
          allocationId={selectedAircraft.allocationId}
          aircraftId={selectedAircraft.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Arrival Dialog */}
      {selectedAircraft && (
        <ArrivalDialog
          open={arrivalOpen}
          onOpenChange={setArrivalOpen}
          tailNumber={selectedAircraft.aircraft.tailNumber}
          allocationId={selectedAircraft.allocationId}
          aircraftId={selectedAircraft.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Schedule Create Dialog */}
      {selectedAircraft && (
        <ScheduleEditDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          schedule={null}
          aircraftId={selectedAircraft.aircraft.id}
          locationId={locationId ?? undefined}
        />
      )}
    </>
  )
}

// ── Section Header ──

const sectionColors = {
  blue: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-300',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300',
  gray: 'bg-muted/50 border-border text-muted-foreground',
  purple: 'bg-purple-50 border-purple-200 text-purple-700 dark:bg-purple-950/30 dark:border-purple-800 dark:text-purple-300',
  amber: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-300',
}

function Section({
  title,
  icon,
  count,
  color,
  children,
}: {
  title: string
  icon: React.ReactNode
  count: number
  color: 'blue' | 'green' | 'gray' | 'purple' | 'amber'
  children: React.ReactNode
}) {
  return (
    <div>
      <div className={cn('flex items-center gap-2 px-4 py-2 border-b text-xs font-medium', sectionColors[color])}>
        {icon}
        {title}
        <Badge variant="outline" className="ml-auto text-[10px] h-4 px-1">
          {count}
        </Badge>
      </div>
      <div className="divide-y">{children}</div>
    </div>
  )
}

// ── Arrival Card ──

function ArrivalCard({
  item,
  showCheckIn,
  onFocus,
  onCheckIn,
  checkInPending,
}: {
  item: SectionItem
  showCheckIn?: boolean
  onFocus: () => void
  onCheckIn?: () => void
  checkInPending?: boolean
}) {
  const { data, dateLabel, dateTooltip } = item
  const badge = getContractBadge(data)
  const clientName = getClientName(data)
  const isOverdue = data.allocation.expectedReturnAt
    ? getDaysUntil(data.allocation.expectedReturnAt) < 0
    : false

  return (
    <div className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors">
      <div className="flex items-start justify-between">
        <button className="text-left flex-1 min-w-0" onClick={onFocus}>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{data.aircraft.tailNumber}</span>
            <span className="text-xs text-muted-foreground">{data.aircraft.typeCode}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {clientName}
          </div>
        </button>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {badge && (
            <Badge className={cn('text-[10px] px-1.5', badge.className)}>
              {badge.label}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-1.5">
        <span
          className={cn(
            'text-xs',
            isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'
          )}
          title={dateTooltip}
        >
          {dateLabel}
        </span>

        {showCheckIn && onCheckIn && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={onCheckIn}
            disabled={checkInPending}
          >
            <LogIn className="w-3 h-3 mr-1" />
            {checkInPending ? 'Checking in...' : 'Check In'}
          </Button>
        )}
      </div>
    </div>
  )
}

// ── Schedule Card (for schedule-only items, no allocation) ──

function ScheduleCard({ schedule }: { schedule: AircraftSchedule }) {
  const tailNumber = schedule.aircraft?.tailNumber ?? 'Unknown'
  const typeCode = schedule.aircraft?.typeCode ?? ''
  const time = new Date(schedule.scheduledAt)
  const timeLabel = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const airportLabel = schedule.airportIcao
    ? schedule.airportName
      ? `${schedule.airportIcao} — ${schedule.airportName}`
      : schedule.airportIcao
    : null

  return (
    <div className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{tailNumber}</span>
            {typeCode && <span className="text-xs text-muted-foreground">{typeCode}</span>}
          </div>
          {airportLabel && (
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {schedule.direction === 'inbound' ? 'From' : 'To'} {airportLabel}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <Badge variant="outline" className="text-[10px] px-1.5">
            {SCHEDULE_SOURCE_LABELS[schedule.source] ?? schedule.source}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-muted-foreground">{timeLabel}</span>
        {schedule.confidence !== 'confirmed' && (
          <Badge variant="secondary" className="text-[10px] px-1 h-4">
            {SCHEDULE_CONFIDENCE_LABELS[schedule.confidence]}
          </Badge>
        )}
        {schedule.flightReference && (
          <span className="text-[10px] text-muted-foreground">
            {schedule.flightReference}
          </span>
        )}
      </div>
    </div>
  )
}
